import type sv from './sv'

type DeepString<T> = T extends string
  ? string
  : { readonly [K in keyof T]: DeepString<T[K]> }

const en: DeepString<typeof sv> = {
  title: 'Create your App',
  description:
    'Build your app on top of Convex SaaS, explore the documentation and start your journey.',
  nav: {
    dashboard: 'Dashboard',
    jobs: 'Jobs',
    candidates: 'Candidates',
    questionBank: 'Question Bank',
    templates: 'Templates',
    settings: 'Settings',
    billing: 'Billing',
    documentation: 'Documentation',
    personalAccount: 'Personal Account',
    topUpBalance: 'Top Up Balance',
    theme: 'Theme',
    language: 'Language',
    logOut: 'Log Out',
  },
  header: {
    dashboard: {
      title: 'Dashboard',
      description: 'Manage your Apps and view your usage.',
    },
    settings: {
      title: 'Settings',
      description: 'Manage your account settings.',
    },
    jobs: {
      title: 'Jobs',
      description: 'Manage your job postings and interview templates.',
    },
    billing: {
      title: 'Billing',
      description: 'Manage your balance and purchase interviews.',
    },
    organisation: {
      title: 'Organisation',
      description: 'Manage organisation settings.',
    },
    team: {
      title: 'Team',
      description: 'Manage team members and permissions.',
    },
    interviews: {
      title: 'Interview Report',
      description: 'View candidate interview results and AI analysis.',
    },
    questionBank: {
      title: 'Question Bank',
      description: 'Save and reuse interview questions across jobs.',
    },
    templates: {
      title: 'Job Templates',
      description: 'Save and reuse job configurations as templates.',
    },
    candidates: {
      title: 'Candidates',
      description: 'Search and filter candidates across all jobs.',
    },
  },
  auth: {
    login: {
      title: 'Continue to Convex SaaS',
      subtitle: 'Welcome back! Please log in to continue.',
      emailLabel: 'Email',
      emailPlaceholder: 'Email',
      emailInvalid: 'Email address is not valid.',
      continueWithEmail: 'Continue with Email',
      termsText: 'By clicking continue, you agree to our',
      termsOfService: 'Terms of Service',
      and: 'and',
      privacyPolicy: 'Privacy Policy.',
      ssoSignIn: 'Sign in with SSO',
      ssoOr: 'or',
      ssoRedirecting: 'Redirecting to your identity provider…',
      ssoNotConfigured: 'SSO is not configured for this email domain.',
    },
    verify: {
      title: 'Check your inbox!',
      subtitle:
        "We've just emailed you a temporary password. Please enter it below.",
      codePlaceholder: 'Code',
      codeLabel: 'Code',
      codeMinLength: 'Code must be at least 8 characters.',
      continue: 'Continue',
      didNotReceive: 'Did not receive the code?',
      requestNewCode: 'Request New Code',
      codeSent: 'A new code has been sent.',
      invalidCode: 'Invalid or expired code.',
    },
  },
  onboarding: {
    username: {
      title: 'Welcome!',
      subtitle: "Let's get started by setting up your profile.",
      organizationNamePlaceholder: 'Organization name',
      organizationNameLabel: 'Organization name',
      firstNamePlaceholder: 'First name',
      firstNameLabel: 'First name',
      lastNamePlaceholder: 'Last name',
      lastNameLabel: 'Last name',
      usernamePlaceholder: 'Username',
      usernameLabel: 'Username',
      continue: 'Continue',
      hint: 'You can update your username at any time from your account settings.',
    },
  },
  jobs: {
    createJob: 'Create Job',
    noJobs: 'No jobs yet',
    noJobsDescription: 'Create your first job to get started.',
    statusDraft: 'Draft',
    statusActive: 'Active',
    statusPaused: 'Paused',
    duplicate: 'Duplicate',
    pause: 'Pause',
    pauseConfirm: 'Are you sure?',
    delete: 'Delete',
    deleteConfirm: 'Are you sure?',
    form: {
      titleLabel: 'Title',
      titlePlaceholder: 'e.g. Backend Engineer',
      descriptionLabel: 'Description',
      descriptionPlaceholder:
        'Describe the role and what you are looking for...',
      languageLabel: 'Interview language',
      languageSv: 'Swedish',
      languageEn: 'English',
      languageNo: 'Norwegian',
      languageDa: 'Danish',
      languageFi: 'Finnish',
      languageBoth: 'Swedish & English',
      allowedModalitiesLabel: 'Allowed modalities',
      videoLabel: 'Video',
      videoRequired: 'Required',
      videoOptional: 'Optional',
      videoDisabled: 'Disabled',
      audioLabel: 'Audio',
      audioRequired: 'Required',
      audioDisabled: 'Disabled',
      chatLabel: 'Chat',
      chatEnabled: 'Enabled',
      chatDisabled: 'Disabled',
      expiryDaysLabel: 'Link expiry (days)',
      interviewCountLabel: 'Number of interviews',
      topNLabel: 'Top N candidates to highlight',
      save: 'Save',
      create: 'Create Job',
      cancel: 'Cancel',
    },
    tabs: {
      overview: 'Overview',
      questions: 'Questions',
      screener: 'Screener',
      additionalInfo: 'Additional Info',
      modality: 'Modality',
      voice: 'Voice',
      candidates: 'Candidates',
      candidateActions: 'Actions',
      report: 'Report',
      settings: 'Settings',
    },
    overview: {
      name: 'Name',
      email: 'Email',
      status: 'Status',
      score: 'Score',
      report: 'Report',
      noScore: '—',
      viewReport: 'View report',
      shortlist: 'Shortlist',
      shortlistedOnly: 'Shortlisted only',
      shareShortlist: 'Share shortlist',
      copyLink: 'Copy link',
      linkCopied: 'Link copied!',
    },
    shortlist: {
      title: 'Shortlisted candidates',
      jobLabel: 'Role',
      name: 'Name',
      status: 'Status',
      score: 'Score',
      noScore: '—',
      empty: 'No candidates have been shortlisted yet.',
    },
    launch: {
      button: 'Launch job',
      topUpAndLaunch: 'Top up & Launch',
      active: 'Active',
      insufficientBalance:
        'You need at least {{required}} SEK to launch. Current balance: {{balance}} SEK',
    },
    preview: {
      button: 'Preview interview',
      banner: 'This is a preview — no credit will be deducted.',
      loading: 'Creating preview…',
      error: 'Could not start preview. Please try again.',
      close: 'Close',
      completed: 'Preview ended.',
      screener: {
        title: 'Pre-screening questions',
        continue: 'Continue',
      },
      additionalInfo: {
        title: 'Additional information',
        required: '*',
        continue: 'Continue',
      },
      briefing: {
        aboutToStart: 'You are about to start an AI-conducted video interview.',
        expectedDuration:
          'Expected duration: approximately {{minutes}} minutes.',
        quietEnvironment:
          'Please ensure you are in a quiet environment with a working microphone.',
        consentText: 'I consent to being recorded during this interview.',
        termsOfService: 'Terms of Service',
        privacyPolicy: 'Privacy Policy',
        starting: 'Starting…',
        ready: "I'm ready",
      },
      disqualified: {
        title: 'Thank you for your interest',
        body: 'Unfortunately you do not meet the requirements for this position.',
      },
      room: {
        connecting: 'The interviewer is joining…',
      },
    },
    voice: {
      loading: 'Loading voices...',
      noResults: 'No voices match your filters.',
      filterLanguage: 'Language',
      filterAccent: 'Accent',
      filterCategory: 'Category',
      filterGender: 'Gender',
      filterAge: 'Age',
    },
    screener: {
      questionPlaceholder: 'e.g. Do you have a valid driving license?',
      optionPlaceholder: 'Option',
      singleSelect: 'Single select',
      multiChoice: 'Multi-choice',
      markDisqualifying: 'Mark as disqualifying',
      disqualifyingHint:
        'Check an option to disqualify candidates who select it.',
      addOption: 'Add option',
      removeOption: 'Remove option',
      addQuestion: 'Add Question',
      remove: 'Remove',
      save: 'Save Screener',
      saving: 'Saving...',
      saved: 'Saved',
      empty: 'No screener questions yet. Add your first question below.',
      drag: 'Drag to reorder',
    },
    additionalInfo: {
      labelPlaceholder: 'e.g. Portfolio URL',
      placeholderPlaceholder: 'e.g. https://...',
      typeText: 'Text',
      typeNumber: 'Number',
      typeDate: 'Date',
      typeUrl: 'URL',
      typeEmail: 'Email',
      requiredLabel: 'Required',
      addField: 'Add Field',
      remove: 'Remove',
      save: 'Save Fields',
      saving: 'Saving...',
      saved: 'Saved',
      empty: 'No fields yet. Add your first field below.',
      drag: 'Drag to reorder',
    },
    questions: {
      textPlaceholder: 'Write your interview question...',
      competencyPlaceholder: 'Competency (optional)',
      followUpPlaceholder: 'Follow-up question...',
      addQuestion: 'Add Question',
      addFollowUp: 'Add Follow-up',
      showFollowUps: 'Follow-ups ({{count}})',
      hideFollowUps: 'Hide follow-ups',
      remove: 'Remove',
      removeFollowUp: 'Remove follow-up',
      saveToBank: 'Save to bank',
      addFromBank: 'Add from bank',
      save: 'Save Questions',
      saving: 'Saving...',
      saved: 'Saved',
      empty: 'No questions yet. Add your first question below.',
      drag: 'Drag to reorder',
    },
    locked: {
      notice:
        'This cannot be changed while the job is active. Pause the job to make changes.',
    },
    generate: {
      title: 'Generate with AI',
      tabText: 'Paste text',
      tabUrl: 'URL',
      tabPdf: 'PDF',
      textPlaceholder: 'Paste a job description here...',
      urlPlaceholder: 'https://example.com/job-posting',
      pdfLabel: 'Choose PDF',
      pdfSelected: '{{name}}',
      generate: 'Generate',
      generating: 'Generating...',
      addToJob: 'Add',
      discard: 'Discard',
      addAll: 'Add all',
      discardAll: 'Discard all',
      error: 'Failed to generate questions. Please try again.',
    },
    settings: {
      reminderCadenceTitle: 'Reminder cadence',
      reminderCadenceDescription:
        'Set which days after the invite a reminder should be sent. Leave empty to use the default schedule (halfway, day before, last day).',
      reminderCadenceDefault:
        'Using default schedule (halfway, day before, last day).',
      reminderDay: 'Day {{day}}',
      addReminderDay: 'Add day',
      removeReminderDay: 'Remove reminder day',
      reminderDayPlaceholder: 'Day (e.g. 2)',
      competencyWeightsTitle: 'Competency weights',
      competencyWeightsDescription:
        'Assign a relative weight to each competency when computing the average score. Default weight is 1 (equal weighting).',
      competencyWeightsNoCompetencies:
        'No competencies defined yet. Add competencies to your interview questions first.',
      competencyWeightsLocked:
        'Weights cannot be changed once the job is active.',
      introMessageTitle: 'Intro message',
      introMessageDescription:
        'Shown to candidates on the briefing screen before the interview starts. Leave empty to use the default text.',
      introMessagePlaceholder: 'Add a personalised intro message…',
      outroMessageTitle: 'Outro message',
      outroMessageDescription:
        'Shown to candidates on the completion screen after the interview ends. Leave empty to use the default text.',
      outroMessagePlaceholder: 'Add a personalised outro message…',
      translationsTitle: 'Translations',
      translationsDescription:
        'Provide job content in all interview languages. Use the translate button to generate a draft, then review and save.',
      candidateFeedbackTitle: 'Candidate feedback',
      candidateFeedbackDescription:
        'Show candidates their personalised competency scores after completing the interview.',
      candidateFeedbackEnabled: 'Show feedback to candidates',
    },
    translations: {
      generating: 'Generating…',
      translateFrom: 'Translate from {{language}}',
      draftHint: 'AI draft — review and edit, then click outside to save.',
      swedish: 'Swedish',
      english: 'English',
    },
  },
  questionBank: {
    addQuestion: 'Add Question',
    textPlaceholder: 'Write your interview question...',
    competencyPlaceholder: 'Competency (optional)',
    save: 'Save',
    empty: 'No questions yet. Add your first question below.',
    delete: 'Delete',
    deleteConfirm: 'Are you sure?',
  },
  interviews: {
    header: {
      title: 'Interview Report',
      description: 'View candidate interview results and AI analysis.',
    },
    candidates: {
      noInterviews: 'No interviews yet.',
      statusPending: 'Pending',
      statusInProgress: 'In Progress',
      statusCompleted: 'Completed',
      statusFailed: 'Failed',
      viewReport: 'View Report',
      duration: '{{min}}m {{sec}}s',
      capacity: '{{used}} / {{total}} slots used',
      increaseCapacity: 'Increase capacity',
      increaseCapacityTitle: 'Increase interview capacity',
      increaseCapacityDescription:
        'Each additional slot costs {{cost}} credits. Your current balance is {{balance}} credits.',
      additionalSlots: 'Additional slots',
      increaseCapacityConfirm: 'Confirm',
      increaseCapacityCancel: 'Cancel',
      increaseCapacityInsufficientBalance:
        'Insufficient balance. You need {{required}} credits but only have {{balance}}.',
    },
    candidateActions: {
      calendlyUrlLabel: 'Calendly URL',
      calendlyUrlDescription:
        'Paste your Calendly link to send it to candidates who are advanced.',
      saveCalendlyUrl: 'Save',
      noCandidates: 'No candidates yet.',
      statusInvited: 'Invited',
      statusStarted: 'Started',
      statusCompleted: 'Completed',
      statusExpired: 'Expired',
      statusDisqualified: 'Disqualified',
      statusRejected: 'Rejected',
      statusAdvanced: 'Advanced',
      reject: 'Reject',
      advance: 'Advance',
      reTake: 'Allow re-take',
      rejectDialogTitle: 'Send rejection email',
      rejectDialogDescription:
        'This will send a rejection email to the candidate. You can add an optional personal message below.',
      rejectCustomMessagePlaceholder: 'Optional personal message...',
      rejectConfirm: 'Send rejection',
      advanceConfirmTitle: 'Advance candidate',
      advanceConfirmDescription:
        'This will send the candidate a Calendly link to book a human interview.',
      advanceConfirm: 'Advance',
      reTakeConfirmTitle: 'Allow re-take',
      reTakeConfirmDescription:
        'This will reset the candidate invite link and extend the deadline. The candidate will receive a new link.',
      reTakeConfirm: 'Allow re-take',
      cancel: 'Cancel',
      selected: '{{count}} selected',
      bulkReject: 'Reject selected',
      bulkAdvance: 'Advance selected',
      bulkSendReminder: 'Send reminder',
      bulkRejectDialogTitle: 'Reject selected candidates',
      bulkRejectDialogDescription:
        'This will send a rejection email to {{count}} candidate(s). You can add an optional personal message below.',
      bulkRejectConfirm: 'Reject all',
    },
    report: {
      back: 'Back to Job',
      summary: 'Summary',
      noSummary: 'Analysis in progress...',
      competencies: 'Competency Scores',
      noScores: 'No scores yet.',
      rationale: 'Rationale',
      quotes: 'Notable Quotes',
      noQuotes: 'No quotes.',
      transcript: 'Transcript',
      noTranscript: 'No transcript available.',
      duration: 'Duration',
      completedAt: 'Completed',
      shareLabel: 'Share report',
      shareEnabled: 'Sharing enabled',
      shareDisabled: 'Sharing disabled',
      copyLink: 'Copy link',
      linkCopied: 'Copied!',
      video: 'Video Recording',
      noVideo: 'No video recording available.',
      videoLoading: 'Loading video...',
      clips: 'Competency Clips',
      noClips: 'No clips available yet.',
      clipDuration: '{{sec}}s',
      reviewedBy: 'Reviewed by',
      noReviewers: 'No one has reviewed this report yet.',
      notes: 'Notes',
      noNotes: 'No notes yet.',
      addNote: 'Add note',
      notePlaceholder: 'Write a note…',
      deleteNote: 'Delete',
      noteAdded: 'Note added.',
      aiDetection: 'AI Response Detection',
      aiDetectionPending: 'Analysis in progress...',
      aiDetectionLow: 'Responses appear human',
      aiDetectionMedium: 'Some AI-like patterns detected',
      aiDetectionHigh: 'Responses likely AI-generated',
      aiDetectionScore: 'Likelihood score',
      aiDetectionFlags: 'Flagged segments',
    },
    chat: {
      title: 'Ask about the candidate',
      placeholder:
        "Ask a question about the candidate's interview responses...",
      inputPlaceholder: 'Type a question...',
      send: 'Send',
      sending: 'Sending...',
      errorMessage: 'Something went wrong. Please try again.',
    },
    orgChat: {
      title: 'Ask about candidates',
      placeholder: 'Ask a question about any candidate across all jobs...',
      inputPlaceholder: 'e.g. Who scored highest on problem solving?',
      send: 'Send',
      sending: 'Sending...',
      errorMessage: 'Something went wrong. Please try again.',
      toggle: 'AI Chat',
    },
    publicReport: {
      notFound: 'This report is no longer available.',
      notFoundDescription:
        'The link may have expired or sharing has been disabled.',
    },
    pdfExport: {
      downloadButton: 'Download',
      aggregatedReport: 'Download Aggregated Report',
      transcripts: 'Download Transcripts',
      individualReports: 'Download Individual Reports',
      drawerTitleTranscripts: 'Download Transcripts',
      drawerTitleReports: 'Download Individual Reports',
      drawerDescription: 'Select candidates to include in the download.',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      downloadSelected: 'Download selected',
      downloadAll: 'Download all',
      generating: 'Generating {{completed}} of {{total}}...',
      complete: 'Download ready',
      error: 'Export failed. Please try again.',
      noCompletedCandidates: 'No completed interviews to export.',
      cancel: 'Cancel',
    },
    aggregateReport: {
      noCompletedInterviews: 'No completed interviews yet.',
      rank: 'Rank',
      candidate: 'Candidate',
      averageScore: 'Avg. Score',
      topN: 'Top {{n}}',
      competencies: 'Competency Breakdown',
    },
    candidateFeedback: {
      title: 'Your results',
      preparing: 'Your personalised feedback is being prepared…',
      competencies: 'Competency scores',
      scoreLabel: '{{score}} / 10',
    },
  },
  candidateSearch: {
    searchPlaceholder: 'Search by name or email…',
    filterJob: 'All jobs',
    filterStatus: 'All statuses',
    filterMinScore: 'Min score',
    filterMaxScore: 'Max score',
    filterFromDate: 'From date',
    filterToDate: 'To date',
    clearFilters: 'Clear filters',
    noResults: 'No candidates found.',
    columnName: 'Name',
    columnJob: 'Job',
    columnStatus: 'Status',
    columnScore: 'Avg. Score',
    columnDate: 'Invited',
    viewReport: 'View report',
    scoreValue: '{{score}}',
    noScore: '—',
  },
  notifications: {
    title: 'Notifications',
    markAllRead: 'Mark all as read',
    empty: 'No notifications yet.',
    typeInterviewCompleted: 'Interview completed',
    typeInterviewFailed: 'Interview failed',
    typeLowBalance: 'Low balance',
    typeJobActivated: 'Job activated',
    typeCandidateExpired: 'Candidate expired',
  },
  dashboard: {
    getStarted: 'Get Started',
    getStartedDescription:
      'Explore the Dashboard and get started with your first app.',
    tipChangeLanguage: 'TIP: Try changing the language!',
    exploreDocumentation: 'Explore Documentation',
    statsInvited: 'Invited',
    statsCompleted: 'Completed',
    statsAdvanced: 'Advanced',
    statsAvgScore: 'Avg. score',
    analytics: {
      title: 'Analytics',
      jobTitle: 'Job',
      status: 'Status',
      completionRate: 'Completion rate',
    },
    accountLocked: {
      message:
        'Your subscription has ended. Reactivate to continue running interviews.',
      reactivate: 'Reactivate',
    },
  },
  settings: {
    avatar: {
      title: 'Your Avatar',
      description: 'This is your avatar. It will be displayed on your profile.',
      hint: 'Click on the avatar to upload a custom one from your files.',
      reset: 'Reset',
    },
    profile: {
      title: 'Your Profile',
      description:
        'Your first and last name are shown to candidates and colleagues.',
      firstNamePlaceholder: 'First name',
      lastNamePlaceholder: 'Last name',
      hint: 'Use your real name.',
      save: 'Save',
    },
    username: {
      title: 'Your Username',
      description:
        'This is your username. It will be displayed on your profile.',
      placeholder: 'Username',
      hint: 'Please use 32 characters at maximum.',
      save: 'Save',
    },
    deleteAccount: {
      title: 'Delete Account',
      description:
        'Permanently delete your account, all of your projects, and all related data.',
      hint: 'This action cannot be undone, proceed with caution.',
      confirm: 'Are you sure?',
      delete: 'Delete Account',
    },
    contactSupport: {
      title: 'Contact Support',
      description: 'Report a bug, ask a question, or request a feature.',
      topicLabel: 'Topic',
      topicBug: 'Bug',
      topicTechnicalIssue: 'Technical issue',
      topicBilling: 'Billing',
      topicGeneral: 'General question',
      topicFeatureRequest: 'Feature request',
      severityLabel: 'Severity',
      severityLow: 'Low',
      severityMedium: 'Medium',
      severityHigh: 'High',
      severityCritical: 'Critical',
      subjectLabel: 'Subject',
      subjectPlaceholder: 'Brief description of the issue',
      messageLabel: 'Message',
      messagePlaceholder: 'Describe your issue in detail…',
      submit: 'Submit',
      submitting: 'Submitting…',
      successTitle: 'Request received',
      successMessage: 'Your ticket has been submitted. Reference: {{ticketId}}',
      hint: "We'll get back to you as soon as possible.",
    },
    tabs: {
      general: 'General',
      billing: 'Billing',
      organisation: 'Organisation',
      branding: 'Branding',
      team: 'Team',
      notifications: 'Notifications',
    },
    branding: {
      title: 'Branding',
      description: 'Customise how your organisation appears to candidates.',
      logoTitle: 'Logo',
      logoDescription: 'Shown in the candidate interview briefing screen.',
      uploadLogo: 'Upload Logo',
      uploading: 'Uploading...',
      primaryColorTitle: 'Primary Color',
      primaryColorDescription:
        'Applied to buttons and accents in the candidate flow.',
      websiteTitle: 'Website',
      websiteDescription:
        'Shown to candidates as a link to learn more about your company.',
      taglineTitle: 'Tagline',
      taglineDescription:
        'A short description of your company shown in the briefing screen.',
      taglinePlaceholder: 'e.g. We build the future of work',
      hint: 'These settings are visible to candidates.',
      save: 'Save',
      saving: 'Saving...',
      saved: 'Saved',
    },
    notifications: {
      title: 'Notification Preferences',
      description: 'Choose which email notifications you receive.',
      lowBalanceLabel: 'Low balance',
      lowBalanceDescription:
        'Receive an email when your interview balance drops to 5 or fewer interviews.',
      interviewCompletedLabel: 'Interview completed',
      interviewCompletedDescription:
        'Receive an email when a candidate completes their interview.',
      reportReadyLabel: 'Report ready',
      reportReadyDescription:
        'Receive an email when the AI analysis for an interview is complete.',
      teamMemberJoinedLabel: 'Team member joined',
      teamMemberJoinedDescription:
        'Receive an email when a new team member joins your organisation.',
    },
    team: {
      title: 'Team Members',
      description: "Manage your team's access to the organisation.",
      inviteTitle: 'Invite Team Member',
      inviteDescription: 'Send an invitation via email.',
      emailLabel: 'Email',
      emailPlaceholder: 'colleague@example.com',
      roleLabel: 'Role',
      roleMember: 'Member',
      roleAdmin: 'Admin',
      roleOwner: 'Owner',
      invite: 'Invite',
      inviteSent: 'Invitation sent.',
      membersTitle: 'Current Members',
      remove: 'Remove',
      removeDialogTitle: 'Remove team member',
      removeDialogDescription:
        'Are you sure you want to remove {{name}} from the organisation?',
      removeConfirm: 'Remove',
      cancel: 'Cancel',
      hint: "Invited users will have access to the organisation's resources.",
    },
    organisation: {
      title: 'Organisation Settings',
      description: 'Settings visible to candidates.',
      displayNameTitle: 'Display Name',
      displayNameDescription:
        'This name is shown to candidates in the interview portal.',
      displayNamePlaceholder: 'Organisation name',
      termsUrlTitle: 'Terms of Service URL',
      termsUrlDescription: 'Shown to candidates in interview invitations.',
      termsUrlPlaceholder: 'https://example.com/terms',
      privacyUrlTitle: 'Privacy Policy URL',
      privacyUrlDescription: 'Shown to candidates in interview invitations.',
      privacyUrlPlaceholder: 'https://example.com/privacy',
      hint: 'These settings are visible to candidates.',
      save: 'Save',
      sso: {
        title: 'Single Sign-On (SSO)',
        description:
          "Allow team members to sign in using your organisation's identity provider.",
        emailDomainTitle: 'Email Domain',
        emailDomainDescription:
          'The email domain used to identify SSO users (e.g. acme.com).',
        emailDomainPlaceholder: 'acme.com',
        discoveryUrlTitle: 'OIDC Discovery URL',
        discoveryUrlDescription:
          'The well-known OpenID Connect configuration URL for your identity provider.',
        discoveryUrlPlaceholder:
          'https://your-idp.example.com/.well-known/openid-configuration',
        clientIdTitle: 'Client ID',
        clientIdDescription:
          'The client ID registered in your identity provider.',
        clientIdPlaceholder: 'client-id',
        clientSecretTitle: 'Client Secret',
        clientSecretDescription:
          'The client secret registered in your identity provider.',
        clientSecretPlaceholder: '••••••••',
        clientSecretSetOn: 'Set on {{date}}',
        clientSecretRotate: 'Rotate',
        clientSecretRemove: 'Remove',
        saveConfig: 'Save SSO Configuration',
        enforceTitle: 'Enforce SSO',
        enforceDescription:
          'Require team members to sign in via SSO. Organisation owners are always exempt.',
        enforced: 'Enforced',
        notEnforced: 'Not enforced',
        hint: 'SSO configuration is only visible to organisation owners.',
      },
    },
  },
  support: {
    contactForm: {
      nameLabel: 'Name',
      namePlaceholder: 'Your name',
      emailLabel: 'Email',
      emailPlaceholder: 'your@email.com',
      successTitle: 'Message sent',
      successMessage:
        "We've received your message and will get back to you shortly.",
      errorGeneric: 'Something went wrong. Please try again.',
    },
  },
  billing: {
    balanceTitle: 'Your Balance',
    balanceDescription: 'Available balance for interviews.',
    subscriptionTitle: 'Subscription',
    subscriptionDescription:
      'Start your 14-day free trial. No charge until the trial ends.',
    subscriptionNote: 'You will be redirected to Polar to complete sign-up.',
    subscriptionStatusActive: 'Active',
    subscriptionStatusTrialing: 'Trial',
    subscribeLabel: 'Start Free Trial',
    manageSubscription: 'Manage Subscription',
    trialEndsOn: 'Trial ends on {{date}}',
    topUpTitle: 'Top Up Balance',
    topUpDescription: 'Select an amount to add to your account.',
    topUpNote: 'You will be redirected to Polar to complete payment.',
    topUpLabel: 'Top Up',
    invoiceHistoryTitle: 'Invoice History',
    invoiceHistoryDescription: 'View your past purchases and invoices.',
    redirectedToPolar: 'You will be redirected to the Polar Customer Portal.',
    viewInvoices: 'View Invoices',
    billingEmailTitle: 'Billing Email',
    billingEmailDescription:
      'Receipts and invoices from Polar are sent to this address.',
    billingEmailPlaceholder: 'billing@example.com',
    billingEmailSave: 'Save',
    billingEmailSaved: 'Saved.',
    billingEmailHint:
      'Leave blank to use your account email. Only admins and owners can change this.',
    autoTopUpTitle: 'Auto Top-Up',
    autoTopUpDescription:
      'Automatically top up your balance when it drops below a threshold.',
    autoTopUpEnabled: 'Enabled',
    autoTopUpDisabled: 'Disabled',
    autoTopUpNoPaymentMethod:
      'No saved payment method on file. Complete a purchase first to enable auto top-up.',
    autoTopUpThresholdLabel: 'Top up when fewer than',
    autoTopUpThresholdUnit: 'interviews remain',
    autoTopUpProductLabel: 'Top-up package',
    autoTopUpSave: 'Save',
    autoTopUpSaved: 'Saved.',
  },
  checkout: {
    title: 'Payment Complete',
    description: 'Your payment has been processed and your balance updated.',
    completed: 'Checkout completed!',
    returnToDashboard: 'Return to Dashboard',
  },
  templates: {
    empty: 'No templates yet',
    emptyDescription: 'Save an existing job as a template to reuse it.',
    questionCount: 'Questions',
    screenerCount: 'Screener',
    interviewCount: 'Interviews',
    useTemplate: 'Use template',
    saveAsTemplate: 'Save as template',
    saveAsTemplateTitle: 'Save as template',
    templateNameLabel: 'Template name',
    templateNamePlaceholder: 'e.g. Senior Backend Engineer',
    saveButton: 'Save',
    createFromTemplateTitle: 'Create job from "{{name}}"',
    createJobButton: 'Create job',
  },
  candidate: {
    loading: 'Loading…',
    invalid: {
      title: 'Link not valid',
      body: 'This invite link has expired or is no longer valid.',
    },
    orgLocked: {
      title: 'Account inactive',
      body: "This organisation's account is currently inactive. Please contact the recruiter for assistance.",
    },
    disqualified: {
      title: 'Thank you for your interest',
      body: 'Unfortunately you do not meet the requirements for this position.',
    },
    screener: {
      title: 'Pre-screening questions',
      continue: 'Continue',
    },
    additionalInfo: {
      title: 'Additional information',
      continue: 'Continue',
    },
    briefing: {
      howToConduct: 'How would you like to conduct this interview?',
      usesModality: 'This interview uses',
      aboutToStart: 'You are about to start an AI-conducted interview.',
      expectedDuration: 'Expected duration: approximately {{minutes}} minutes.',
      quietEnvironment:
        'Please ensure you are in a quiet environment with a working microphone.',
      aiNotice:
        'Your answers will be assessed by an AI system. A human recruiter reviews all AI-generated results and makes every hiring decision independently — no automated decision is ever made about your application.',
      requestScores:
        'You may request your competency scores and rationale at any time by emailing',
      consentRecording: 'I consent to being recorded during this interview.',
      consentAi:
        'I consent to my responses being processed by AI during this interview.',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
      starting: 'Starting…',
      ready: "I'm ready",
      insufficientBalance:
        'The interview could not be started at this time. Please contact the recruiter.',
      orgLockedError:
        "This organisation's account is currently inactive. Please contact the recruiter.",
    },
    modality: {
      video: { label: 'Video', description: 'Camera and microphone' },
      audio: { label: 'Audio only', description: 'Microphone only' },
      chat: { label: 'Text', description: 'Typed responses' },
    },
    room: {
      connecting: 'The interviewer is joining…',
      questionOf: 'Question {{index}} of {{total}}',
      answered: '{{completed}} of {{total}} answered',
      chatPlaceholder: 'Type your answer…',
      chatSend: 'Send',
      chatLeave: 'Leave',
    },
    completed: {
      title: 'Interview complete',
      defaultOutro: 'Thank you for your time. We will be in touch soon.',
      feedbackQuestion: 'How was your experience?',
      feedbackComment: 'Any additional comments? (optional)',
      feedbackSubmit: 'Submit feedback',
      feedbackThanks: 'Thanks for your feedback.',
    },
    abandoned: {
      title: 'Interview ended',
      body: 'You have left the interview. If you meant to do this, no further action is required.',
    },
    error: {
      title: 'Something went wrong',
      body: 'An unexpected error occurred during your interview. Please try again or contact support.',
      reportLink: 'Report a technical issue',
      severityTitle: 'How severe was the issue?',
      severityLow: 'Minor — I could still continue',
      severityMedium: 'Significant — the interview was disrupted',
      severityHigh: 'Critical — I could not complete the interview',
      describeTitle: 'Describe what happened',
      describePlaceholder: 'e.g. Audio stopped working, page froze…',
      submit: 'Submit report',
      submitting: 'Submitting…',
      submitted: 'Your report has been submitted. Thank you.',
    },
  },
  ai: {
    generate: 'Generate',
  },
}

export default en
